import {
  takeEvery,
  takeLatest,
  debounce,

  put,
  take,
  fork,
  cancel,
} from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import { LOCATION_CHANGE, matchPath } from "connected-react-router";
import {
  getFilterByLocationSearch,
  getQueryByFilter,
  withLabelsFrom,
  RESOLVABLE_TYPES,
} from "./urlFilters";
import { createUrlFilterSagas } from "../../utils/redux/urlFilterSagas";
import { createResolveFilterLabels } from "../../utils/redux/resolveFilterLabels";
import { subscribeVacancies } from "../../utils/cable";

// CREW-1169 — the query string is the single source of truth for the
// vacancies filter, the same contract the task manager got in CREW-1040/A2:
//   URL --getFilterByLocationSearch--> filter  (on LOCATION_CHANGE)
//   filter --getQueryByFilter--> URL           (on APPLY_FILTER)
//
// This file replaces the generic CRUD kit's two fetch triggers rather than
// adding to them (see the watcher list at the bottom). The kit fetches on both
// APPLY_FILTER and LOCATION_CHANGE; leaving those in place would mean two list
// requests for every apply and every page open — the first one fired before
// the filter had been hydrated from the URL, i.e. for the wrong query.
//
// Search deliberately stays out of the URL: it is already persisted per user
// in localStorage (`vacancies.search`), and putting it in the address bar
// would make a shared link carry somebody else's half-typed vessel name.

export const basePageUrl = "/vacancies";

// Typing resets the page, then hands over to the URL sync. Defined as a factory
// for the same reason the two sagas above are: this file hands `actions` in.
export const createSearchToUrl = (actions) => function* searchToUrl() {
  yield put(actions.applySearch()); // reset current pagination page

  yield* urlFilterSagas(actions).syncFilterToUrl();
};

const selectFeature = (state) => state.vacancies;

// The two sagas themselves live in utils/redux/urlFilterSagas (CREW-1024 gave
// /vessels the same contract, and one copy is the point). These two factories
// keep the shape this file has always exported.
const urlFilterSagas = (actions) =>
  createUrlFilterSagas({
    basePageUrl,
    selectFeature,
    urlFilters: { getFilterByLocationSearch, getQueryByFilter, withLabelsFrom },
    actions,
  });

/**
 * pageInit
 * Hydrate the filter from the URL and fetch — on first render, in-app
 * navigation, back/forward, a shared link, a preset chip, and our own push
 * from syncFilterToUrl. Labels are carried over from the applied filter
 * (CREW-1208), because the URL carries ids only.
 */
export const createPageInit = (actions) => urlFilterSagas(actions).pageInit;

/**
 * syncFilterToUrl
 * Applying a filter writes it into the URL; the resulting LOCATION_CHANGE
 * hydrates and fetches. Same URL → fetch directly, no navigation.
 */
export const createSyncFilterToUrl = (actions) => urlFilterSagas(actions).syncFilterToUrl;

// ---------------------------------------------------------------
// Live updates (CREW-1205)
//
// /vacancies is a shared surface: several recruiters keep it open and work
// the same rows. The backend pushes a row whenever its assignees, its
// candidates or its state change, and we merge that into the list in place.
// A push, not a "reload the list" ping — ten open tabs would otherwise mean
// ten refetches per click.
// ---------------------------------------------------------------

// What the socket is allowed to write into a row. A whitelist rather than a
// spread: the payload also carries `type`, and a field added server-side
// should not silently start overwriting a column here.
// `slot_open` rides along because a closing vacancy is exactly the event this
// channel exists for, and the stripe decides "closed vs overdue" by it.
const LIVE_ROW_FIELDS = ["state", "slot_open", "assignees", "candidates"];

export const toRowPatch = (data) => {
  const patch = { id: data.id };

  LIVE_ROW_FIELDS.forEach((key) => {
    if (data[key] !== undefined) patch[key] = data[key];
  });

  return patch;
};

export const createVacanciesChannel = () =>
  eventChannel((emit) => subscribeVacancies({ onVacancy: (data) => emit(data || {}) }));

export const createLiveUpdates = (actions) =>
  function* liveUpdates() {
    const channel = createVacanciesChannel();

    try {
      while (true) {
        const data = yield take(channel);

        // Events for rows this page is not showing are ignored by the
        // reducer; dropping the malformed ones here keeps it simple.
        if (data && data.id !== undefined && data.id !== null) {
          yield put(actions.patchItem(toRowPatch(data)));
        }
      }
    } finally {
      channel.close();
    }
  };

/**
 * watchListPageLive
 * The subscription lives exactly as long as the vacancies page is open. The
 * non-exact path match keeps it alive behind the form modals
 * (/vacancies/5/edit), which render over the list rather than replacing it —
 * the same reason `didOpenListPage` treats them as the list.
 */
export const createWatchListPageLive = (actions) => {
  const liveUpdates = createLiveUpdates(actions);

  return function* watchListPageLive() {
    let task = null;

    while (true) {
      const {
        payload: {
          location: { pathname },
        },
      } = yield take(LOCATION_CHANGE);

      const onListPage = Boolean(matchPath(pathname, { path: basePageUrl }));

      if (onListPage && !task) {
        task = yield fork(liveUpdates);
      } else if (!onListPage && task) {
        yield cancel(task);
        task = null;
      }
    }
  };
};

/**
 * getVacanciesSagas
 * The feature's COMPLETE watcher list — it is returned in place of the generic
 * kit's, not alongside it (see `extends.js`). Everything the kit does that is
 * unrelated to filtering is re-declared here against the kit's own workers, so
 * only the filter path differs from the default CRUD behaviour.
 * @param {Function} baseSagas getCRUDSagas' result, with its workers attached
 * @param {Object} constants
 * @param {Object} actions
 * @returns {Array} watcher effects
 */
export default function getVacanciesSagas(baseSagas, constants, actions) {
  const pageInit = createPageInit(actions);
  const syncFilterToUrl = createSyncFilterToUrl(actions);
  const searchToUrl = createSearchToUrl(actions);
  // CREW-1209 — watches SET_FILTER rather than hooking into pageInit, so it
  // covers every way a filter can arrive from a URL (cold link, reload,
  // back/forward) and stays out of the fetch path. After an Apply the labels
  // are already there and it asks for nothing.
  const resolveFilterLabels = createResolveFilterLabels({
    selectFeature,
    types: RESOLVABLE_TYPES,
    actions,
  });

  return [
    // APPLY_FILTER is deliberately absent: it goes through the URL now.
    takeLatest(
      [
        constants.SORT_ITEMS,
        constants.CHANGE_PAGE_NUMBER,
        constants.CHANGE_PAGE_ITEMS_COUNT,
        constants.LOAD_ITEMS_REQUEST,
      ],
      baseSagas.fetchItems
    ),
    takeEvery(constants.DELETE_ITEM_REQUEST, baseSagas.deleteItem),
    takeEvery(constants.CREATE_ITEM_REQUEST, baseSagas.createItem),
    takeEvery(constants.UPDATE_ITEM_REQUEST, baseSagas.updateItem),
    // Search goes through the URL like every other filter (see
    // utils/redux/urlFilterSagas): reset the page, write the query string, and
    // let LOCATION_CHANGE hydrate and fetch. The kit's own fetchAfterSearch
    // fetches directly, which is exactly how the box and the address bar used
    // to disagree.
    debounce(700, constants.CHANGE_SEARCH, searchToUrl),
    takeEvery(constants.APPLY_FILTER, syncFilterToUrl),
    takeLatest(constants.SET_FILTER, resolveFilterLabels),
    takeEvery(LOCATION_CHANGE, pageInit),
    fork(createWatchListPageLive(actions)),
  ];
}
